import { FileText } from 'lucide-react';
import { FeaturedArticle, usePublishedArticles } from '@/features/articles';
import { useSiteSettings } from '@/features/settings';
import { NewsletterBand } from '@/features/newsletter';
import { ArticleCard } from '@/entities/article';
import { EmptyState, ErrorState, Skeleton } from '@/shared/ui';
import { SITE } from '@/shared/config/site';
import { ROUTES } from '@/shared/config/routes';
import { Seo, blogSchema } from '@/shared/seo';

export function HomePage() {
  const { data: articles, isPending, isError, refetch } = usePublishedArticles();
  const { data: settings } = useSiteSettings();

  // Display copy may be edited in /admin/settings; the *name* never is.
  const siteName = SITE.name;
  const tagline = settings?.tagline?.trim() || SITE.tagline;
  const list = articles ?? [];
  const featured = list[0];
  const rest = list.slice(1);

  // No <main> here: RootLayout already provides the one main landmark, and a
  // nested second one is invalid HTML that confuses assistive tech and crawlers.
  return (
    <>
      {/*
        Brand first, then the strapline: the words that distinguish this site
        survive Google's ~60-character title truncation. The description is the
        code constant rather than the editable tagline so the rendered head
        matches the static HTML a crawler sees before JS runs.
      */}
      <Seo
        fullTitle={`${SITE.name} — ${SITE.tagline}`}
        description={SITE.description}
        path={ROUTES.home}
        jsonLd={[
          blogSchema(
            list.map((article) => ({
              slug: article.slug,
              title: article.title,
              excerpt: article.excerpt,
              publishedAt: article.publishedAt,
              authorName: article.author?.name ?? null,
            })),
          ),
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-zinc-950">
        <div className="pointer-events-none absolute inset-0 flex select-none items-center justify-center overflow-hidden">
          <span
            className="whitespace-nowrap font-display font-black italic leading-none text-white"
            style={{ fontSize: 'clamp(5rem,22vw,20rem)', opacity: 0.03 }}
          >
            {siteName}
          </span>
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-20">
          <p className="mb-3 font-mono text-[10px] font-medium uppercase tracking-widest text-amber-400">
            Audit, explained
          </p>
          <h1 className="font-display text-5xl font-black italic leading-none tracking-tight text-white sm:text-6xl">
            {siteName}
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-zinc-400">{tagline}</p>
        </div>
      </section>

      {/* Articles */}
      <section className="mx-auto max-w-5xl px-6 py-14">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-mono text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            Writing
          </span>
          <div className="h-px flex-1 bg-border" />
          <span className="font-mono text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            {list.length} article{list.length === 1 ? '' : 's'}
          </span>
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-2xl" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : list.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No articles yet"
            message="Once articles are published they’ll appear here."
          />
        ) : (
          <div className="space-y-4">
            {featured ? <FeaturedArticle article={featured} /> : null}
            {rest.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {rest.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </section>

      {/* Newsletter */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        <NewsletterBand
          title="One audit lesson a week."
          subtitle="New article or case breakdown every week. No jargon, no paywall."
        />
      </section>
    </>
  );
}
